import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { INPUT_CLASSES } from '@/lib/theme'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import athleteApi, { findSchoolForEmail, type SchoolOption } from '@/lib/api'
import SchoolPicker from '@/components/SchoolPicker'
import { CURRENT_LEGAL_VERSION } from '@adthlete/shared-frontend'

const MINIMUM_ATHLETE_AGE = 18

function ageOf(dateOfBirth: string) {
  const dob = new Date(dateOfBirth)
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const m = now.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1
  return age
}

/**
 * Athletes who joined from the website gave only a name and email, so this is where they give
 * their school, confirm they are old enough and accept the Terms of Use and Privacy Policy, the
 * things the signup form collects. Only what's missing is asked. It comes before the rest of the
 * app; the school decides whether they have the Creator Lab.
 */
const AcceptTerms: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser, logout } = useAuth()
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [school, setSchool] = useState<SchoolOption | null>(null)

  const needsTerms = athlete?.terms_required !== false
  const needsSchool = !!athlete?.school_required
  const email = athlete?.email ?? ''
  // A school email (name@umd.edu) picks the school for them
  useEffect(() => {
    if (!needsSchool || !email) return
    let cancelled = false
    findSchoolForEmail(email).then((s) => {
      if (!cancelled && s) setSchool((current) => current ?? s)
    })
    return () => {
      cancelled = true
    }
  }, [needsSchool, email])

  const firstName = athlete?.name?.split(' ')[0] ?? 'there'
  const validDate = !!dateOfBirth && !Number.isNaN(new Date(dateOfBirth).getTime())
  const oldEnough = validDate && ageOf(dateOfBirth) >= MINIMUM_ATHLETE_AGE
  const canSubmit =
    (!needsTerms || (oldEnough && agreed)) && (!needsSchool || !!school) && !saving

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    try {
      if (needsSchool && school) {
        await athleteApi.patch('/athlete/school', { school_id: school.id })
      }
      if (needsTerms) {
        await athleteApi.post('/users/me/terms', {
          date_of_birth: dateOfBirth,
          agreed_to_terms: true,
          legal_version: CURRENT_LEGAL_VERSION,
        })
      }
      await refreshUser()
      navigate('/welcome', { replace: true })
    } catch (err: any) {
      toast.error('Could not save', {
        description: err?.response?.data?.error ?? 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-10">
      <div className="fixed inset-0 -z-10 bg-background" />
      <div className="w-full max-w-md">
        <img
          src="https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png"
          alt="Athios"
          className="mx-auto mb-8 h-7"
        />
        <div className="rounded-[28px] bg-[#FFFFFF] p-6 shadow-[0_20px_60px_rgba(16,24,40,0.08)]">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4d7c0f]">
            One quick thing
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
            Welcome, {firstName}.
          </h1>
          <p className="mt-2 text-gray-600">
            {needsSchool && needsTerms
              ? 'Tell us your school, confirm your date of birth and accept our terms to continue.'
              : needsSchool
                ? 'Tell us your school to continue.'
                : 'Confirm your date of birth and accept our terms to continue.'}
          </p>

          <form onSubmit={submit} data-track-form="Accept terms" className="mt-6 space-y-4">
            {needsSchool ? (
              <div className="space-y-1.5">
                <Label htmlFor="school" className="text-gray-700">
                  School
                </Label>
                <SchoolPicker id="school" value={school} onChange={setSchool} />
              </div>
            ) : null}
            {needsTerms ? (
              <>
            <div className="space-y-1.5">
              <Label htmlFor="dob" className="text-gray-700">
                Date of birth
              </Label>
              <Input
                id="dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className={INPUT_CLASSES.dark}
                autoComplete="bday"
              />
              {validDate && !oldEnough && (
                <p className="text-xs text-red-600">
                  You must be at least {MINIMUM_ATHLETE_AGE} years old to create an athlete
                  account.
                </p>
              )}
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(v === true)}
                className="mt-0.5"
              />
              <Label htmlFor="terms" className="text-sm font-normal leading-snug text-gray-700">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">
                  Terms of Use and Privacy Policy
                </a>
                .
              </Label>
            </div>
              </>
            ) : null}

            <Button
              type="submit"
              variant="lime"
              className="h-12 w-full text-base"
              disabled={!canSubmit}
            >
              {saving ? 'Saving…' : 'Continue'}
            </Button>
          </form>
        </div>
        <p className="mt-4 text-center text-xs text-gray-500">
          Not {firstName}?{' '}
          <button
            type="button"
            onClick={() => {
              logout()
              navigate('/signin')
            }}
            className="text-gray-700 underline"
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  )
}

export default AcceptTerms
